<template>
  <fieldset
    class="ui-base-radio-select"
    :class="{
      'is-layout-row': layout === 'row',
      'is-layout-column': layout === 'column',
    }"
  >
    <BaseRadio
      v-for="(opt, index) in innerOptions"
      v-model="innerValue"
      :disabled="disabled"
      :native-value="innerValueFunction(opt, index)"
      :key="innerValueFunction(opt, index)"
      class="radio-item"
      >{{ innerLabelFunction(opt, index) }}</BaseRadio
    >
  </fieldset>
</template>

<script lang="ts" setup>
const { ls } = useI18n()

interface Props {
  disabled?: boolean
  labelFunction?: Function
  valueFunction?: Function
  modelValue?: any
  options?: any[]
  enum?: EnumDef
  labeledOptions?: { key: string; label: LangString }[]
  layout?: 'row' | 'column'
}
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  valueFunction: (opt, index) => opt,
  labelFunction: (opt, index) => opt,
  options: () => [],
  layout: 'row',
})
const emits = defineEmits(['update:modelValue'])

const innerOptions = computed(() => {
  if (props.labeledOptions) {
    return props.labeledOptions.map(opt => opt.key)
  } else if (props.enum) {
    return Object.keys(props.enum.c)
  } else {
    return props.options
  }
})

const innerValueFunction = computed(() => (opt, index) => {
  if (props.labeledOptions) {
    return props.labeledOptions[index].key
  } else if (props.enum) {
    return opt
  } else {
    return props.valueFunction(opt, index)
  }
})

const innerLabelFunction = computed(() => (opt, index) => {
  if (props.labeledOptions) {
    return ls(props.labeledOptions[index].label)
  } else if (props.enum) {
    return ls(props.enum.c[opt].l)
  } else {
    return props.labelFunction(opt, index)
  }
})

const innerValue = computed({
  get: () => {
    return props.modelValue
  },
  set: v => {
    emits('update:modelValue', v)
  },
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.ui-base-radio-select {
  border: none;

  &.is-layout-row {
    display: flex;
    flex-direction: row;
    gap: 1rem;

    @include is-mobile {
      flex-direction: column;
      gap: 0.25rem;
    }
  }

  &.is-layout-column {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
}

.radio-item {
  display: inline-flex;
  margin: 0;
  padding: 0.5rem 0;
}
</style>
